import { useRef } from "react";
import { Icons } from "@/components/ui/icons";
import type { IconName } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import type { SlashCommand } from "@/lib/slash-commands";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";
import { useT } from "@/i18n";
import { inputBarComposerTipsMessages } from "@/i18n/messages/input-bar-composer-tips";
import { useInputPopoverPlacement } from "./use-input-popover-placement";

interface SlashCommandPopupProps {
  commands: SlashCommand[];
  query: string;
  highlightedIndex: number;
  onSelect: (command: SlashCommand) => void;
}

export function SlashCommandPopup({
  commands,
  query,
  highlightedIndex,
  onSelect,
}: SlashCommandPopupProps) {
  const t = useT(inputBarComposerTipsMessages);
  const ref = useRef<HTMLDivElement>(null);
  const filtered = commands.filter((c) =>
    c.name.toLowerCase().startsWith(query.toLowerCase()),
  );

  const commandItems = filtered.filter((c) => c.type === "command");
  const skillItems = filtered.filter((c) => c.type === "skill");
  const hasVisiblePopup =
    commandItems.length > 0 || skillItems.length > 0 || query.length === 0;
  const style = useInputPopoverPlacement(ref, {
    active: hasVisiblePopup,
    align: "stretch",
  });

  // Show nothing only if query filters out everything
  if (!hasVisiblePopup) return null;

  let globalIdx = 0;

  return (
    <div
      ref={ref}
      className={cn(surfaceRoleClassName("menu"), "absolute overflow-y-auto")}
      style={style}
      data-testid="slash-popup"
      {...surfaceRoleAttributes("menu", {
        nativeLevel: "root",
        nativeGroup: "menus",
      })}
    >
      {/* Commands section */}
      {commandItems.length > 0 && (
        <>
          <div className="border-b border-glass-stroke-subtle px-2.5 py-1.5">
            <span className="text-appkit-mini font-medium uppercase tracking-wide text-muted">
              {t("commands")}
            </span>
          </div>
          <div className="p-1">
            {commandItems.map((cmd) => {
              const idx = globalIdx++;
              const IconComponent = Icons[cmd.icon as IconName];
              return (
                <button
                  key={cmd.name}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect(cmd);
                  }}
                  className={cn(
                    "denker-glass-hover flex w-full items-center gap-2.5 rounded-[16px] px-appkit-sm py-1.5 text-left transition-colors",
                    idx === highlightedIndex && "bg-glass-stroke-light",
                  )}
                  data-testid="slash-command-item"
                >
                  {IconComponent && (
                    <IconComponent className="h-3.5 w-3.5 shrink-0 text-muted" />
                  )}
                  <span className="shrink-0 text-appkit-small font-medium text-primary">
                    /{cmd.name}
                  </span>
                  <span
                    className="min-w-0 truncate text-appkit-mini text-muted"
                    title={cmd.description}
                  >
                    {cmd.description}
                  </span>
                  {cmd.status && (
                    <span
                      data-testid="slash-command-status"
                      className="ml-auto shrink-0 text-appkit-mini font-medium text-muted tabular-nums"
                    >
                      {cmd.status}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Skills section — always shown */}
      <div
        className={cn(
          "px-2.5 py-1.5",
          commandItems.length > 0 && "border-t border-glass-stroke-subtle",
        )}
      >
        <span className="text-appkit-mini font-medium uppercase tracking-wide text-muted">
          {t("skills")}
        </span>
      </div>
      {skillItems.length > 0 ? (
        <div className="p-1">
          {skillItems.map((cmd) => {
            const idx = globalIdx++;
            const IconComponent = Icons[cmd.icon as IconName];
            return (
              <button
                key={cmd.name}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(cmd);
                }}
                className={cn(
                  "denker-glass-hover flex w-full items-center gap-2.5 rounded-[16px] px-appkit-sm py-1.5 text-left transition-colors",
                  idx === highlightedIndex && "bg-glass-stroke-light",
                )}
                data-testid="slash-command-skill-item"
              >
                {IconComponent && (
                  <IconComponent className="h-3.5 w-3.5 shrink-0 text-muted" />
                )}
                <span className="shrink-0 text-appkit-small font-medium text-primary">
                  /{cmd.name}
                </span>
                <span
                  className="min-w-0 truncate text-appkit-mini text-muted"
                  title={cmd.description}
                >
                  {cmd.description}
                </span>
                {cmd.status && (
                  <span
                    data-testid="slash-command-status"
                    className="ml-auto shrink-0 text-appkit-mini font-medium text-muted tabular-nums"
                  >
                    {cmd.status}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="px-2.5 pb-2 pt-0.5">
          <span className="text-appkit-mini text-muted">{t("noSkills")}</span>
        </div>
      )}
    </div>
  );
}
